import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import type { PublicAd } from '@gymboard/types'
import { apiAsset } from '../../lib/api'
import './ad-card.css'

const EASE_OUT = [0.23, 1, 0.32, 1] as const

interface Props {
  ads: PublicAd[]
  /** The ad showing now and its position (from useAdRotation). */
  ad: PublicAd | null
  index: number
  gymName: string
  /** compact: under the next-class card; hero: the whole right column. */
  size: 'compact' | 'hero'
}

/** The gym's own ads on the idle screen, one at a time, crossfading. */
export default function AdCard({ ads, ad, index, gymName, size }: Props) {
  const reduced = useReducedMotion()
  if (!ad) return null

  const blur = reduced ? 'blur(0px)' : 'blur(4px)'
  return (
    <section className={`ad ad-${size}`} aria-label="From the gym">
      <div className="ad-stack">
        <AnimatePresence initial={false}>
          <motion.article
            key={ad.id}
            className={`ad-slide${ad.imageUrl ? '' : ' no-img'}`}
            initial={{ opacity: 0, filter: blur }}
            animate={{ opacity: 1, filter: 'blur(0px)', transition: { duration: 0.6, ease: EASE_OUT } }}
            exit={{ opacity: 0, filter: blur, transition: { duration: 0.35, ease: EASE_OUT } }}
          >
            {ad.imageUrl && <img className="ad-img" src={apiAsset(ad.imageUrl)} alt="" />}
            <div className="ad-body">
              <div className="ad-text">
                <div className="ad-eyebrow">{gymName}</div>
                <h3 dir="auto">{ad.headline}</h3>
                {ad.body && <p dir="auto">{ad.body}</p>}
                {ad.ctaType !== 'NONE' && ad.ctaType !== 'URL' && ad.ctaLabel && (
                  <div className="ad-hint" dir="auto">
                    {ad.ctaLabel} <span>· in the Agaru app</span>
                  </div>
                )}
              </div>
              {ad.ctaType === 'URL' && ad.ctaUrl && (
                <div className="ad-qr">
                  <div className="ad-qr-code">
                    <QRCodeSVG value={ad.ctaUrl} size={256} bgColor="#F1EEE8" fgColor="#0E1022" level="M" />
                  </div>
                  <span dir="auto">{ad.ctaLabel ?? 'Scan me'}</span>
                </div>
              )}
            </div>
          </motion.article>
        </AnimatePresence>
      </div>
      {ads.length > 1 && (
        <div className="ad-dots" aria-hidden>
          {ads.map((a, i) => (
            <i key={a.id} className={i === index ? 'on' : undefined} />
          ))}
        </div>
      )}
    </section>
  )
}
